import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { StandardTooltip } from "../../../../shared/StandardTooltip/StandardTooltip";
import { ArrowRightIcon } from "../../../AnnualOkr/AnnualOkrHelpers";
import {
  dashboardCtaFor,
  dashboardRouteFor,
} from "../../_config/quarterlyOkrRoutes";
import QuarterFlags from "../QuarterFlags/QuarterFlags";
import QuarterRow from "../QuarterRow/QuarterRow";
import "./_client-card.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One client on the dashboard — the Annual OKR / Strategy Canvas card
// (`aok-*`). The row carries TODAY's quarter (by its `start_date` /
// `end_date`) under the company name — its period, flags and one CTA. The
// accordion holds the client's other AI quarters, each with its own button:
// "Next quarter" (starts after today) first, then "Earlier quarters".
// Without a quarter running today, the nearest upcoming one takes the row.
//
// The CTA is always there: disabled with the reason when the quarter has
// nowhere to go yet, so every row lines up and reads the same.
// ═══════════════════════════════════════════════════════════════════════════

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");

const DISABLED_REASON = {
  none: "This client hasn't started Quarterly OKRs with the AI coach yet.",
  notSent: "The CEO hasn't sent this quarter's OKRs to you yet.",
};

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dayOf = (date) => (date ? String(date).slice(0, 10) : null);

// { current, upcoming, earlier } — dates are ISO, so strings compare.
export const splitQuarters = (quarters = [], today = todayIso()) => {
  const withDates = quarters.map((q) => ({
    q,
    start: dayOf(q.start_date),
    end: dayOf(q.end_date),
  }));
  const byStart = (a, b) => String(a.start).localeCompare(String(b.start));
  let current =
    withDates.find(
      ({ start, end }) => start && end && start <= today && today <= end,
    ) || null;
  let upcoming = withDates
    .filter(({ start }) => start && start > today)
    .sort(byStart);
  const earlier = withDates
    .filter(({ end }) => end && end < today)
    .sort((a, b) => byStart(b, a));
  // Nothing running today: the nearest upcoming quarter takes the row.
  if (!current && upcoming.length) {
    [current, ...upcoming] = upcoming;
  }
  // No dates at all (old payload): §9.1 order, latest first.
  if (!current && !upcoming.length && !earlier.length && quarters.length) {
    const [first, ...rest] = quarters;
    return { current: first, upcoming: [], earlier: rest };
  }
  return {
    current: current?.q || null,
    upcoming: upcoming.map(({ q }) => q),
    earlier: earlier.map(({ q }) => q),
  };
};

const ClientCard = ({ client, isOpen, onToggle }) => {
  const history = useHistory();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // §9.1: up to three AI quarters per client.
  const { current, upcoming, earlier } = splitQuarters(client.quarters || []);
  const route = current ? dashboardRouteFor(current) : null;
  const others = upcoming.length + earlier.length;
  const canExpand = others > 0;

  // Measured rather than animated to `auto` — `max-height` can't transition
  // to a keyword (same as the sibling dashboards).
  useEffect(() => {
    setContentHeight(
      isOpen && contentRef.current ? contentRef.current.scrollHeight : 0,
    );
  }, [isOpen, others]);

  const button = (
    <button
      type="button"
      className="aok-view-btn"
      disabled={!route}
      onClick={(event) => {
        event.stopPropagation();
        if (route) history.push(route);
      }}
    >
      {current ? dashboardCtaFor(current) : "Start review"}
      <ArrowRightIcon />
    </button>
  );

  return (
    <div
      className={`aok-card qokr-client-card${isOpen ? " open" : ""}${
        current?.needs_review ? " qokr-client-card--needs-review" : ""
      }`}
    >
      <div
        className={`aok-card-row${canExpand ? "" : " qokr-client-card__row--static"}`}
        onClick={canExpand ? onToggle : undefined}
        onKeyDown={(event) => {
          if (canExpand && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onToggle();
          }
        }}
        role={canExpand ? "button" : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? isOpen : undefined}
      >
        <div className="aok-card-left">
          <div className="aok-avatar">{initialsOf(client.company_name)}</div>
          <div className="aok-info">
            <div className="aok-name">{client.company_name}</div>
            <div className="qokr-client-card__quarter">
              {current ? (
                <>
                  <span className="qokr-client-card__label">
                    {current.label}
                  </span>
                  <QuarterFlags quarter={current} />
                </>
              ) : (
                <span className="aok-ceo">No AI quarter yet</span>
              )}
            </div>
          </div>
        </div>

        <div className="aok-card-actions qokr-client-card__actions">
          {route ? (
            button
          ) : (
            <StandardTooltip
              tooltTipContent={
                current ? DISABLED_REASON.notSent : DISABLED_REASON.none
              }
            >
              <span className="aok-view-btn-wrap">{button}</span>
            </StandardTooltip>
          )}
          {canExpand ? (
            <i
              className={`fa-solid ${
                isOpen ? "fa-chevron-up" : "fa-chevron-down"
              } qokr-client-card__chevron`}
              aria-hidden="true"
            />
          ) : null}
        </div>
      </div>

      {canExpand ? (
        <div className="aok-accordion" style={{ maxHeight: contentHeight }}>
          <div ref={contentRef} className="aok-accordion-inner">
            <div className="aok-accordion-divider" />
            {[
              [
                upcoming.length > 1 ? "Next quarters" : "Next quarter",
                upcoming,
              ],
              ["Earlier quarters", earlier],
            ].map(([title, list]) =>
              list.length ? (
                <div className="qokr-client-card__group" key={title}>
                  <div className="qokr-client-card__past-title">{title}</div>
                  <ul className="qokr-client-card__past">
                    {list.map((quarter) => (
                      <QuarterRow
                        key={quarter.quarter_company_id}
                        quarter={quarter}
                      />
                    ))}
                  </ul>
                </div>
              ) : null,
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ClientCard;
